import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import AccountingToolPage from '@/components/AccountingToolPage'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import { useDefaultIncompleteReportMonth } from '@/hooks/useDefaultIncompleteReportMonth'
import { usePageAccess } from '@/hooks/usePageAccess'
import {
  deleteMonth,
  listAccounts,
  listMonths,
  uploadStatement,
  type BankReconAccount,
  type MonthSummaryItem,
} from '@/services/bankRecon'
import { useMonthCloseNavStore } from '@/store/monthCloseNavStore'
import { toast } from '@/store/toastStore'
import { countNonCompleteMonthOptions } from '@/utils/allocationFormat'
import { formatApiError } from '@/utils/apiError'
import BankReconAccountPanel from './bank-recon/BankReconAccountPanel'
import {
  bankReconStatementFilename,
  planBankReconBulkUploads,
} from './bank-recon/bankReconFormat'
import { mergeBankReconMonthSelectOptions } from './bank-recon/bankReconMonthStatus'
import { accountHasBankReconUpload, parseAccountStatus } from './bank-recon/bankReconRows'

type BadgeTone = 'good' | 'danger' | 'neutral' | 'ready'

type TabBadge =
  | { kind: 'count'; text: string; tone: BadgeTone; label?: string }
  | { kind: 'dot'; tone: 'danger'; label: string }

function tabBadgeClass(tone: BadgeTone): string {
  if (tone === 'good') return 'bg-emerald-100 text-emerald-800'
  if (tone === 'danger') return 'bg-red-100 text-red-800'
  if (tone === 'ready') return 'bg-amber-100 text-amber-800'
  return 'bg-gray-200 text-gray-600'
}

function accountStatusTabBadge(status: string): TabBadge | null {
  const parsed = parseAccountStatus(status)
  if (parsed.kind === 'none' || parsed.kind === 'uploaded') return null
  if (parsed.kind === 'exceptions') {
    return { kind: 'count', text: String(parsed.exceptionCount), tone: 'danger' }
  }
  if (parsed.kind === 'clean') {
    return {
      kind: 'count',
      text: '✓',
      tone: 'ready',
      label: 'Ready to mark reconciled',
    }
  }
  return { kind: 'count', text: '✓', tone: 'good', label: 'Marked reconciled' }
}

function BankReconTabNav({
  accounts,
  activeKey,
  onSelect,
  statusByAccount,
}: {
  accounts: BankReconAccount[]
  activeKey: string | null
  onSelect: (key: string) => void
  statusByAccount: Map<string, string>
}) {
  return (
    <div
      className="inline-flex min-w-max items-center gap-1 rounded-lg bg-gray-200 p-1"
      role="tablist"
      aria-label="Bank accounts"
    >
      {accounts.map((account) => {
        const isActive = activeKey === account.key
        const status = statusByAccount.get(account.key) ?? 'none'
        const badge = accountStatusTabBadge(status)
        return (
          <button
            key={account.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(account.key)}
            className={`flex min-w-[4.5rem] items-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all ${
              isActive ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {account.key}
            {badge?.kind === 'dot' ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-red-500"
                aria-label={badge.label}
                title={badge.label}
              />
            ) : null}
            {badge?.kind === 'count' ? (
              <span
                className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tabBadgeClass(badge.tone)}`}
                title={badge.label}
                aria-label={badge.label}
              >
                {badge.text}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function BankReconUploadModal({
  open,
  reportMonth,
  accounts,
  uploading,
  uploadProgress,
  onClose,
  onUploadFiles,
}: {
  open: boolean
  reportMonth: string
  accounts: BankReconAccount[]
  uploading: boolean
  uploadProgress?: string
  onClose: () => void
  onUploadFiles: (files: FileList | File[]) => void
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dragCounterRef = useRef(0)
  const [dragging, setDragging] = useState(false)
  const dropDisabled = uploading

  const applyFiles = (files: FileList | File[]) => {
    if (dropDisabled) return
    onUploadFiles(files)
  }

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (dropDisabled) return
    dragCounterRef.current += 1
    if (event.dataTransfer.items?.length) setDragging(true)
  }

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (dropDisabled) return
    dragCounterRef.current -= 1
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setDragging(false)
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = dropDisabled ? 'none' : 'copy'
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current = 0
    setDragging(false)
    if (dropDisabled) return
    applyFiles(event.dataTransfer.files)
  }

  if (!open) return null

  const accountKeys = accounts.map((account) => account.key).join(', ')
  const exampleFilename = bankReconStatementFilename(
    reportMonth,
    accounts[0]?.key ?? '0213',
  )

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bank-recon-upload-title"
        className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="bank-recon-upload-title" className="text-sm font-semibold text-gray-900">
              Upload statements
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              PDF only for {reportMonth}. BookKeeper parses statements; QBO is not modified.
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div
          className={`mt-4 rounded-lg border-2 border-dashed px-4 py-6 transition-colors ${
            dragging
              ? 'border-brand-500 bg-brand-50'
              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
          } ${uploading ? 'opacity-60' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={(event) => {
              if (event.target.files) applyFiles(event.target.files)
              if (fileInputRef.current) fileInputRef.current.value = ''
            }}
          />
          <div className="flex flex-col items-center gap-2 text-center">
            <CloudArrowUpIcon className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600">
              Drop all statement PDFs for {reportMonth} here, or{' '}
              <button
                type="button"
                className="font-medium text-brand-700 hover:underline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                browse
              </button>
            </p>
            <p className="text-xs text-gray-500">
              Name files YYYYMM_####.pdf ({accountKeys}), e.g. {exampleFilename}
            </p>
            {uploading && uploadProgress ? (
              <p className="text-sm font-medium text-gray-700">{uploadProgress}</p>
            ) : null}
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="outline" size="md" onClick={onClose} disabled={uploading}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function BankRecon() {
  const { canEdit } = usePageAccess('bank-recon')
  const [accounts, setAccounts] = useState<BankReconAccount[]>([])
  const [monthsSummary, setMonthsSummary] = useState<MonthSummaryItem[]>([])
  const [monthsSummaryReady, setMonthsSummaryReady] = useState(false)
  const [activeAccountKey, setActiveAccountKey] = useState<string | null>(null)
  const [bulkUploading, setBulkUploading] = useState(false)
  const [bulkUploadProgress, setBulkUploadProgress] = useState<string | undefined>()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [removingAccountKey, setRemovingAccountKey] = useState<string | null>(null)
  const [panelRefreshVersion, setPanelRefreshVersion] = useState(0)

  const monthSelectOptions = useMemo(
    () => mergeBankReconMonthSelectOptions(monthsSummary),
    [monthsSummary],
  )
  const setBankReconCount = useMonthCloseNavStore((s) => s.setBankReconCount)

  useEffect(() => {
    if (!monthsSummaryReady) return
    setBankReconCount(countNonCompleteMonthOptions(monthSelectOptions))
  }, [monthsSummaryReady, monthSelectOptions, setBankReconCount])

  const [selectedMonth, setSelectedMonth] = useDefaultIncompleteReportMonth(
    monthSelectOptions,
    monthsSummaryReady,
  )

  const monthEntry = useMemo(
    () => monthsSummary.find((entry) => entry.report_month === selectedMonth) ?? null,
    [monthsSummary, selectedMonth],
  )

  const statusByAccount = useMemo(() => {
    const map = new Map<string, string>()
    for (const account of monthEntry?.accounts ?? []) {
      map.set(account.account_key, account.status)
    }
    return map
  }, [monthEntry])

  const hasMissingUpload = useMemo(
    () =>
      accounts.some(
        (account) => !accountHasBankReconUpload(statusByAccount.get(account.key) ?? 'none'),
      ),
    [accounts, statusByAccount],
  )

  const loadMonthsSummary = useCallback(async () => {
    try {
      const data = await listMonths()
      setMonthsSummary(data.months)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load months'))
      setMonthsSummary([])
    } finally {
      setMonthsSummaryReady(true)
    }
  }, [])

  const loadAccounts = useCallback(async () => {
    try {
      const data = await listAccounts()
      setAccounts(data.accounts)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load accounts'))
      setAccounts([])
    }
  }, [])

  useEffect(() => {
    void loadAccounts()
    void loadMonthsSummary()
  }, [loadAccounts, loadMonthsSummary])

  useEffect(() => {
    if (accounts.length === 0) {
      setActiveAccountKey(null)
      return
    }
    setActiveAccountKey((current) => {
      if (current && accounts.some((account) => account.key === current)) return current
      return accounts[0]?.key ?? null
    })
  }, [accounts])

  const activeAccount = useMemo(
    () => accounts.find((account) => account.key === activeAccountKey) ?? null,
    [accounts, activeAccountKey],
  )

  const handleBulkUpload = useCallback(
    async (files: FileList | File[]) => {
      if (!canEdit || !selectedMonth || bulkUploading) return
      const plan = planBankReconBulkUploads(
        files,
        accounts.map((account) => account.key),
        selectedMonth,
      )
      for (const message of plan.unmatched) {
        toast.error(message)
      }
      for (const accountKey of plan.duplicateAccounts) {
        toast.error(`Multiple PDFs matched account ${accountKey}; using the last file dropped.`)
      }
      if (plan.uploads.size === 0) return

      const uploadEntries = Array.from(plan.uploads.entries())
      setBulkUploading(true)
      setBulkUploadProgress(undefined)
      let successCount = 0
      try {
        for (let index = 0; index < uploadEntries.length; index += 1) {
          const [accountKey, file] = uploadEntries[index]!
          setBulkUploadProgress(
            `Uploading ${index + 1} of ${uploadEntries.length}…`,
          )
          try {
            await uploadStatement(accountKey, selectedMonth, file)
            successCount += 1
            await loadMonthsSummary()
            setPanelRefreshVersion((version) => version + 1)
          } catch (err) {
            toast.error(formatApiError(err, 'Failed to upload statement'))
          }
        }
        if (successCount > 0) {
          toast.success(
            successCount === 1
              ? 'Statement uploaded'
              : `${successCount} statements uploaded`,
          )
          setUploadModalOpen(false)
        }
      } finally {
        setBulkUploading(false)
        setBulkUploadProgress(undefined)
      }
    },
    [accounts, bulkUploading, canEdit, loadMonthsSummary, selectedMonth],
  )

  const handleRemoveUpload = useCallback(
    async (accountKey: string) => {
      if (!canEdit || !selectedMonth || removingAccountKey) return
      const account = accounts.find((entry) => entry.key === accountKey)
      const confirmed = window.confirm(
        `Remove the uploaded statement for ${account?.label ?? accountKey} (${selectedMonth})? Overrides and QBO snapshot for this month will be removed.`,
      )
      if (!confirmed) return
      setRemovingAccountKey(accountKey)
      try {
        await deleteMonth(accountKey, selectedMonth, true)
        toast.success('Statement removed')
        await loadMonthsSummary()
        setPanelRefreshVersion((version) => version + 1)
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to remove statement'))
      } finally {
        setRemovingAccountKey(null)
      }
    },
    [accounts, canEdit, loadMonthsSummary, removingAccountKey, selectedMonth],
  )

  return (
    <AccountingToolPage
      title="Bank Recon"
      description="Exception-driven bank reconciliation: compare statement PDFs to read-only QuickBooks register data. Overrides and reconciled flags live in BookKeeper only — QuickBooks is never written."
      headerExtra={
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label htmlFor="bank-recon-month" className="sr-only">Month</label>
              <ReportMonthSelect
                id="bank-recon-month"
                options={monthSelectOptions}
                value={selectedMonth}
                onChange={(id) => setSelectedMonth(id)}
                disabled={!monthSelectOptions.length}
                placeholder="Select month"
                className="w-[300px] max-w-full text-sm"
              />
            </div>
            {canEdit && selectedMonth && accounts.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="h-10"
                disabled={!hasMissingUpload || bulkUploading}
                onClick={() => setUploadModalOpen(true)}
              >
                <CloudArrowUpIcon className="mr-1.5 h-5 w-5" />
                Upload
              </Button>
            ) : null}
          </div>
          {accounts.length > 0 ? (
            <BankReconTabNav
              accounts={accounts}
              activeKey={activeAccountKey}
              onSelect={setActiveAccountKey}
              statusByAccount={statusByAccount}
            />
          ) : null}
        </div>
      }
    >
      {selectedMonth && activeAccount ? (
        <BankReconAccountPanel
          key={`${activeAccount.key}:${selectedMonth}:${panelRefreshVersion}`}
          accountKey={activeAccount.key}
          accountLabel={activeAccount.label}
          reportMonth={selectedMonth}
          canEdit={canEdit}
          removingUpload={removingAccountKey === activeAccount.key}
          onRemoveUpload={
            canEdit ? () => void handleRemoveUpload(activeAccount.key) : undefined
          }
          onDetailChange={loadMonthsSummary}
        />
      ) : (
        <p className="text-sm text-gray-500">Select a month to begin.</p>
      )}
      {canEdit && selectedMonth && accounts.length > 0 ? (
        <BankReconUploadModal
          open={uploadModalOpen}
          reportMonth={selectedMonth}
          accounts={accounts}
          uploading={bulkUploading}
          uploadProgress={bulkUploadProgress}
          onClose={() => setUploadModalOpen(false)}
          onUploadFiles={(files) => void handleBulkUpload(files)}
        />
      ) : null}
    </AccountingToolPage>
  )
}
